import type { ReactNode } from 'react';
import CoralHeader from './CoralHeader';
import TabBar from './TabBar';
import { useTabParam } from './useTabParam';

interface TabShellProps {
  beanCount?: number; // gold-bean readout in the header (customer shell only)
  eyebrow: string;
  levelTitle: string;
  statusLabel: string;
  chip?: ReactNode;
  xpPercent?: number;
  headerVariant?: 'coral' | 'grey';
  homeContent: ReactNode;
  passContent: ReactNode;
  lootContent: ReactNode;
  moreContent: ReactNode;
  banners?: ReactNode;
  overlays?: ReactNode;
}

export default function TabShell({
  beanCount, eyebrow, levelTitle, statusLabel, chip, xpPercent, headerVariant,
  homeContent, passContent, lootContent, moreContent, banners, overlays,
}: TabShellProps) {
  const { activeTab, selectTab } = useTabParam();
  const content = { home: homeContent, pass: passContent, loot: lootContent, more: moreContent }[activeTab];
  // App frame per the mockup: header and tab bar pinned, only the content
  // between them scrolls. HOME is sized to fit without scrolling.
  return (
    <div className="max-w-md mx-auto flex flex-col h-full bg-background">
      <CoralHeader
        eyebrow={eyebrow}
        title={levelTitle}
        statusLabel={statusLabel}
        chip={chip}
        xpPercent={xpPercent}
        beanCount={beanCount}
        variant={headerVariant}
      />
      {banners && <div className="shrink-0 px-[22px] pt-4">{banners}</div>}
      {/* Tighter top padding and inter-card gap than the original pt-6/gap-3.5:
          the cards stack from the top, so every pixel saved here is a pixel the
          mug gets. BrewStation sits in a flex-1 block below them and was being
          squeezed to the point of being hard to see.
          Tightened again 2026-08-11 (Nik, on-device): HOME still overflowed on
          a 390x844 phone and the mug — the primary action — was clipped by the
          tab bar, so it took a scroll to reach. See the height budget in that
          commit message; this file gives back 9px of it.
          Tightened once more 2026-08-12 (Nik: "we haven't really fixed the home
          page", mug still clipped): pt-2.5 -> pt-1.5 lifts the first card
          toward the header, and gap-2 -> gap-1.5 saves 2px per seam. With five
          HOME cards that is 4px + 8px, and the real reclaim this round comes
          from ShopSpecials collapsing three rows into one. */}
      <main className="flex-1 min-h-0 flex flex-col gap-1.5 px-[22px] pt-1.5 pb-2 overflow-y-auto overscroll-contain">{content}</main>
      <TabBar active={activeTab} onSelect={selectTab} />
      {overlays}
    </div>
  );
}
